// ═══════════════════════════════════════════════════════════════════════════
// lecturaDirectaConCota — la cota de §7.B para la lectura IMPERATIVA
// ═══════════════════════════════════════════════════════════════════════════
//
// EL HUECO QUE CIERRA (medido el 1-oct-2026, report du3xh8vn8Gmd8fmmf5aB)
// ──────────────────────────────────────────────────────────────────────
// `constraints.md §7.B` decide sobre **toda lectura de colección**: tres formas
// de declarar hasta dónde lee, y ninguna otra. Pero la única implementación de
// esas formas vivía en `useDashboardData`, que es un hook con `onSnapshot`. Una
// lectura imperativa de una sola vez —`await getDocs(...)` dentro del
// `Promise.all` de carga de una pantalla— no tenía NINGUNA: ni la 2 (`cap: N`)
// ni la 3 (el opt-out con motivo). El ADR `2026-08-16-que-cuenta-como-cota-de-lectura…`
// ya declaraba su implementación pendiente; esto es esa implementación, no una
// ampliación de la regla. Que el hueco se pagaba está escrito en otro lado:
// `useCargosFullPeriodo.js` documenta una lectura agregada a la que se RENUNCIÓ
// por no tener cómo declararla.
//
// ⭐ POR QUÉ ESTO VIVE ACÁ Y NO EN `cotaDeLectura.js`
// ─────────────────────────────────────────────────
// Porque ese archivo es PURO —sin React y sin Firestore— y lo importan ~30
// módulos de `src/modules/**`; su propio test declara esa premisa en la primera
// línea. Un `import … from 'firebase/firestore'` allá haría que esas 30 suites
// arrastren el SDK. La alternativa era inyectar el lector por argumento, y eso
// es un seam que cualquier caller llena con lo que quiera. Dos archivos: la
// REGLA en el lib puro, el LECTOR acá. No hay dos fuentes — el umbral,
// `resolverCota`, `superaUmbral` y los dos mensajes (con la forma de su ctx y el
// por qué de sus TRES argumentos) siguen viviendo una sola vez, allá.
//
// ⭐⭐ Y POR QUÉ EL `cap` SE ESCRIBE COMO LITERAL EN CADA CALL SITE
// ───────────────────────────────────────────────────────────────
// Parece un detalle de estilo y no lo es. `useProductos.js` lo dejó medido sobre
// el AST: *"el checker verifica que la CLAVE `cap` exista, nunca su valor, así
// que un wrapper con `cap: opts.cap` deja al checker contando cero para
// cualquiera que lo llame … es exactamente el agujero que el checker se escribió
// para cerrar"*. Un helper que recibe el `cap` por argumento y lo reenvía
// convierte al helper en la vía barata para NO declarar nada.
//
// Por eso `scripts/check-cota-de-lectura.cjs` trata la llamada a este helper
// igual que trata a `useDashboardData`: **la CUENTA como lectura sin cota** si su
// primer argumento no es un objeto literal con `cap`. No es "si pasa por el
// helper, salteá" — eso sería fail-open y el ratchet bajaría sin que nada lo
// verifique. Un wrapper encima de ESTE wrapper vuelve a quedar ciego, y por eso
// no hay ninguno.
//
// FALLA CERRADO EN LOS DOS EJES
// ─────────────────────────────
//  · **No recorta, nunca.** Decisión de Mariano del 16-ago: *"el recorte no
//    elige, y por lo tanto no sabemos qué deja afuera"*. El cap GRITA.
//  · **Sin cota declarada grita igual** (`MENSAJE_SIN_COTA`), y en dev tira. Si
//    sólo preguntara `superaUmbral`, un call site sin `cap` daría `false` y el
//    silencio sería total — la clase fail-open que el ADR cita como pagada.
//  · **Rechaza, jamás tira sincrónico.** Varios call sites cuelgan su `.catch`
//    de la promesa ANTES del `Promise.all` (`UsuariosPage.jsx`): un throw salta
//    por encima y deja la pantalla vacía en silencio, que es justo el síntoma
//    que el report du3xh vino a cerrar.
//  · **El error de la lectura se propaga.** Tragarlo convertiría "las rules
//    rechazaron la query" en "el combo está vacío y no sé por qué".
// ═══════════════════════════════════════════════════════════════════════════

import { getDocs } from 'firebase/firestore'
import { createLogger } from '@core/lib/logger'
import {
  resolverCota, superaUmbral, SIN_DECLARAR_EN_PROD,
  MENSAJE_UMBRAL_EXCEDIDO, MENSAJE_SIN_COTA,
} from '@core/lib/cotaDeLectura'

const logger = createLogger('lecturaDirectaConCota')

/**
 * Lee una colección de una sola vez, DECLARANDO hasta dónde lee.
 *
 * Devuelve el snapshot tal cual lo devuelve Firestore —los call sites hacen
 * `snap.docs.map(...)`— y no lo toca: esto no recorta, no ordena y no mapea.
 *
 * @param {object} args
 * @param {string} args.collectionName  nombre de la colección, SÓLO para el
 *        mensaje: sin él, "falta el cap" no le dice a nadie dónde mirar.
 * @param {import('firebase/firestore').Query|import('firebase/firestore').CollectionReference} args.ref
 *        la referencia o query a leer, tal como se la pasarías a `getDocs`.
 * @param {number|symbol} [args.cap]     umbral de ALARMA. No recorta.
 * @param {string} [args.motivoSinCap]   obligatorio si `cap === SIN_CAP_DECLARADO`.
 * @returns {Promise<import('firebase/firestore').QuerySnapshot>}
 * @throws lo que tire `getDocs` — se propaga, como rechazo de la promesa.
 */
export async function leerColeccionConCota({ collectionName, ref, cap = null, motivoSinCap = '' }) {
  // `esDev` se resuelve acá y no se recibe por argumento: un call site que
  // pudiera pasar `esDev: false` apagaría el throw de desarrollo, que es la
  // mitad del control. Mismo criterio que el hook.
  const cota = resolverCota({
    collectionName, cap, motivoSinCap,
    esDev: import.meta.env.DEV,
  })

  // De acá en adelante, los valores RESUELTOS. `SIN_CAP_DECLARADO` es un Symbol
  // y `resolverCota` ya lo tradujo a `cap: null`.
  const snap = await getDocs(ref)

  if (superaUmbral({ recibidos: snap.size, cap: cota.cap })) {
    logger.error(MENSAJE_UMBRAL_EXCEDIDO, null, {
      collectionName, recibidos: snap.size, umbral: cota.cap,
    })
  } else if (cota.motivoDeAlerta === SIN_DECLARAR_EN_PROD) {
    // Llegó a producción sin declarar hasta dónde lee. No se recorta —recortar
    // elegiría por nombre de documento, o sea sin criterio— así que el único
    // costo que se paga es el aviso. Va DESPUÉS de leer, a propósito: así el
    // aviso trae CUÁNTO leyó, que es el dato con el que se le elige un umbral.
    logger.error(MENSAJE_SIN_COTA, null, {
      collectionName, recibidos: snap.size, motivo: SIN_DECLARAR_EN_PROD,
    })
  }

  return snap
}
